import React, { useState } from 'react';
import HeaderLogged from './components/header_logged';
import CalendarSmallBagsProduction from './components/CalendarSmallBagsProduction';
import Container from './components/container';
import SeedsStatusTotals from './components/SeedsStatusTotals';
import SeedsStatusPopup from './components/SeedsStatusPopup';
import SeedNotifications from './components/SeedNotifications';
import LowStockReport from './components/LowStockReport';
import DashboardSection from './components/DashboardSection';
import { API_URL } from './config';
import './seeds-statuses-report.css';
import './dashboard-section.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faCircleHalfStroke, faBoxesStacked, faDollarSign, faSackDollar,
  faMoneyBillWave, faCalendar, faHandHoldingDollar, faUser,
  faSeedling, faBell, faCalendarDays, faTriangleExclamation,
} from '@fortawesome/free-solid-svg-icons';
import { isOff } from './utils/inventoryConsumptionReport';

// Small Bags day popup: seeds used against the norm. Only closed operations are judged,
// an open one has not returned its seeds yet.
const isClosed = (row) => /^complete/i.test(row.status || '');
const seedsOffPlan = (row) => isClosed(row) && isOff(Number(row.seedsNeeded), Number(row.usedSeeds));
const seedsDiff = (row) => {
  if (!isClosed(row)) return 'in production';
  const d = Number(row.usedSeeds) - Number(row.seedsNeeded);
  return (d > 0 ? '+' : '') + d.toFixed(2);
};

const NO_CUSTOM_DATA = [];

function DashboardPage() {
  const [popupData, setPopupData] = useState(null);

  const handleTotalClick = (category) => {
    setPopupData({ category });
  };

  const closePopup = () => {
    setPopupData(null);
  };

  const smallBagsBadgeConfig = [
    {
      key: 'produced',
      label: '',
      icon: <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5B965D', fontSize: '18px' }} />,
      tooltip: 'Produced / Planned'
    },
    {
      key: 'inProduction',
      label: '',
      icon: <FontAwesomeIcon icon={faCircleHalfStroke} style={{ color: '#f5b400', fontSize: '18px' }} />,
      tooltip: 'In Production'
    },
    {
      key: 'planCompletion',
      label: '',
      icon: <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '18px' }} />,
      tooltip: 'Plan Completion Percentage'
    },
    {
      key: 'seedsOff',
      label: '',
      icon: <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#c0392b', fontSize: '18px' }} />,
      tooltip: 'Operations with seeds off plan'
    },
    {
      key: 'smallBagsWithSeedsUsed',
      label: '',
      icon: <FontAwesomeIcon icon={faBoxesStacked} style={{ color: '#2e7d32', fontSize: '18px' }} />,
      tooltip: 'Small Bags With Seeds Used (Used by packers for sets production)'
    },
    {
      key: 'totalLost',
      label: '$',
      icon: <FontAwesomeIcon icon={faMoneyBillWave} style={{ color: '#0288d1', fontSize: '18px' }} />,
      tooltip: 'Total Lost'
    },
    {
      key: 'laborCostPerBag',
      label: '',
      icon: <FontAwesomeIcon icon={faHandHoldingDollar} style={{ color: '#7b1fa2', fontSize: '18px' }} />,
      tooltip: 'Labor Cost per Bag (including Total Lost per Bag)'
    }
  ];

  const setsMicrogreensBadgeConfig = [
    { key: 'produced', label: '', icon: <FontAwesomeIcon icon={faCircleCheck} style={{ color: '#5B965D', fontSize: '18px' }} />, tooltip: 'Produced' },
    { key: 'planCompletion', label: '', icon: <FontAwesomeIcon icon={faCalendar} style={{ fontSize: '18px' }} />, tooltip: 'Plan Completion Percentage' },
    { key: 'whoDidntMeetPlan', label: '', icon: <FontAwesomeIcon icon={faUser} style={{ color: '#d81b60', fontSize: '18px' }} />, tooltip: 'Employees Who Didn\'t Meet Plan' }
  ];

  const smallBagsPopupConfig = {
    enabled: true,
    endpoint: `${API_URL}/api/sbproduction_calendar_details`,
    columns: [
      { label: 'ID', key: 'recID' },
      { label: 'SKU', key: 'sku' },
      { label: 'Name', key: 'packName' },
      { label: 'Employees', key: 'employees' },
      { label: 'QTY', key: 'qty' },
      { label: 'Seeds Needed (lb)', key: 'seedsNeeded', type: 'number', format: (value) => Number(value).toFixed(2) },
      { label: 'Seeds Used (lb)', key: 'usedSeeds', type: 'number', format: (value) => Number(value).toFixed(2), cellClass: (row) => (seedsOffPlan(row) ? 'cell-alert' : '') },
      { label: 'Diff (lb)', key: 'seedsDiff', value: seedsDiff, cellClass: (row) => (seedsOffPlan(row) ? 'cell-alert' : '') },
      { label: 'Overused Seeds Cost ($)', key: 'seedsOverusedCost', type: 'number', format: (value) => Number(value).toFixed(2) },
      { label: 'Damage', key: 'damage' },
      { label: 'Time Spent', key: 'timeSpentHours', type: 'number', format: (value) => Number(value).toFixed(2) },
    ],
    hint: 'Diff = Seeds Used minus Seeds Needed. Red: off the norm by more than 5%. A large minus usually means bags were not added to the operation and are still counted in stock.',
  };

  const setsMicrogreensPopupConfig = {
    enabled: true,
    endpoint: `${API_URL}/api/sets_calendar_details`,
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Name' },
      { key: 'box_count', label: 'Boxes QTY' },
      { key: 'total_qty', label: 'Sets QTY' },
      { key: 'last_added_by', label: 'Employee' },
    ],
  };

  const microgreensWildflowersPopupConfig = {
    enabled: true,
    endpoint: `${API_URL}/api/microgreens_wildflowers_calendar_details`,
    columns: [
      { key: 'sku', label: 'SKU' },
      { key: 'name', label: 'Name' },
      { key: 'box_count', label: 'Boxes QTY' },
      { key: 'total_qty', label: 'Sets QTY' },
      { key: 'last_added_by', label: 'Employee' },
    ],
  };

  // Fertilizers: same popup as Microgreens, plus raw material needed and used per SKU
  const fertilizersPopupConfig = {
    enabled: true,
    endpoint: `${API_URL}/api/fertilizers_calendar_details`,
    columns: [],
    hint: 'Material Needed = unit weight × units packed. Red: raw material used is off by more than 5%.',
  };

  const smallBagsCustomDataConfig = [
    {
      label: 'Small Bags with Seeds',
      endpoint: `${API_URL}/api/smallbags-calendar-data-with-seeds`,
      fields: [
        { key: 'Organo Republic', label: 'OR' },
        { key: 'Seedphony', label: 'SP' }
      ]
    },
    {
      label: 'Small Bags without Seeds',
      endpoint: `${API_URL}/api/smallbags-calendar-data-without-seeds`,
      fields: [
        { key: 'Organo Republic', label: 'OR' },
        { key: 'Seedphony', label: 'SP' }
      ]
    },
    {
      label: 'Average Daily Production',
      endpoint: `${API_URL}/api/smallbags-calendar-data-days-of-production`,
      fields: [
        { key: 'daysOfProduction', label: 'Bags' },
        { key: 'daysOfSupply', label: 'Days of Supply' }
      ]
    }
  ];

  const setsCustomDataConfig = [
    {
      label: 'Average Days of Production',
      endpoint: `${API_URL}/api/sets_days_of_production`,
      fields: [
        { key: 'daysOfProduction', label: 'Days' }
      ]
    }
  ];

  const microgreensWildflowersCustomDataConfig = [
    {
      label: 'Average Days of Production',
      endpoint: `${API_URL}/api/microgreens_wildflowers_days_of_production`,
      fields: [
        { key: 'daysOfProduction', label: 'Days' }
      ]
    }
  ];

  return (
    <>
      <Container>
        <HeaderLogged />
      </Container>
      <div className="dashboard-page">

        <div className="dashboard-tile-row">
          <DashboardSection icon={faSeedling} title="Seeds Overview">
            <SeedsStatusTotals onTotalClick={handleTotalClick} />
          </DashboardSection>

          <DashboardSection icon={faBell} title="Important Notifications">
            <SeedNotifications />
          </DashboardSection>
        </div>

        {popupData && (
          <SeedsStatusPopup
            category={popupData.category}
            onClose={closePopup}
          />
        )}

        <DashboardSection icon={faCalendarDays} title="Production Calendars">
          <div className="dashboard-row">
            <CalendarSmallBagsProduction
              title="Small Bags Production"
              apiEndpoint={`${API_URL}/api/sbproduction_summary_calendar`}
              badgeConfig={smallBagsBadgeConfig}
              popupConfig={smallBagsPopupConfig}
              customDataConfig={smallBagsCustomDataConfig}
            />
            <CalendarSmallBagsProduction
              title="Sets Production"
              apiEndpoint={`${API_URL}/api/sets_summary_calendar`}
              badgeConfig={setsMicrogreensBadgeConfig}
              popupConfig={setsMicrogreensPopupConfig}
              customDataConfig={setsCustomDataConfig}
            />
            <CalendarSmallBagsProduction
              title="Microgreens, Wildflowers Production"
              apiEndpoint={`${API_URL}/api/microgreens_wildflowers_summary_calendar`}
              badgeConfig={setsMicrogreensBadgeConfig}
              popupConfig={microgreensWildflowersPopupConfig}
              customDataConfig={microgreensWildflowersCustomDataConfig}
            />
            <CalendarSmallBagsProduction
              title="Fertilizers Production"
              apiEndpoint={`${API_URL}/api/fertilizers_summary_calendar`}
              badgeConfig={setsMicrogreensBadgeConfig}
              popupConfig={fertilizersPopupConfig}
              customDataConfig={NO_CUSTOM_DATA}
            />
          </div>
        </DashboardSection>

        <DashboardSection id="low-stock-section">
          <LowStockReport />
        </DashboardSection>

      </div>
    </>
  );
}

export default DashboardPage;